import type { Permission } from '@ea/shared';
import * as Popover from '@radix-ui/react-popover';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ViewAsBanner, ViewAsButton } from '@/components/ViewAs';
import { ANNOUNCEMENT_PATHS, AnnouncementCards } from '@/components/Announcements';
import { AlarmClock, Globe, Send, TrendingUp, Banknote, BarChart3, Bell, BookOpen, Building2, CalendarCheck, ClipboardCheck, ClipboardList, CreditCard, FileBarChart, FileSignature, FileText, Gauge, Inbox, Landmark, LayoutDashboard, LifeBuoy, LogOut, Mailbox, MapPin, Menu, MessagesSquare, Moon, Package, Phone, PiggyBank, Radar, Search, Settings, Shield, Sun, Tag, UserCircle, Users, Wallet, X, FileStack, Receipt, Megaphone, MessageCircle, KeyRound, ScrollText } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { relativeTime } from '@/lib/format';
import { useRealtime, useRealtimeStatus } from '@/lib/realtime';
import { Avatar, Badge } from '../ui';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  perm?: Permission;
  anyPerm?: Permission[];
  /** Shown to these roles (instead of a permission). */
  anyRole?: string[];
}

const i = (C: typeof Package) => <C className="size-[18px]" />;

/** A nav link with a query (e.g. /services?type=global_transfer) is active only when that query matches. */
function navQueryMatches(to: string, search: string) {
  const q = to.split('?')[1];
  if (!q) return true;
  const want = new URLSearchParams(q);
  const have = new URLSearchParams(search);
  // The services home defaults to Credit Building when no type is given.
  if (to.startsWith('/services?type=') && !have.get('type')) return want.get('type') === 'credit_building';
  return [...want.entries()].every(([key, v]) => have.get(key) === v);
}

/** Navigation groups; each item is shown only with the right permission (the API enforces it regardless). */
const NAV: { title: string; items: NavItem[] }[] = [
  {
    title: 'Dashboards',
    items: [
      { to: '/workspace', label: 'Operations Workspace', icon: i(LayoutDashboard), perm: 'dashboard.operations' },
      { to: '/management', label: 'Shipping Management', icon: i(Gauge), perm: 'dashboard.shipping_management' },
      { to: '/supervisor', label: 'Supervisor Overview', icon: i(Radar), perm: 'dashboard.supervisor' },
      { to: '/executive', label: 'Executive Overview', icon: i(Shield), perm: 'dashboard.executive' },
      { to: '/payment-processing', label: 'Payment Processing', icon: i(CreditCard), perm: 'dashboard.finance' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/tasks', label: 'Tasks', icon: i(ClipboardList), perm: 'task.read' },
      { to: '/shipments', label: 'Shipments', icon: i(Package), perm: 'shipment.read' },
      { to: '/tracking', label: 'Live Tracking', icon: i(Radar), perm: 'shipment.read' },
      { to: '/labels', label: 'Shipping Labels', icon: i(Tag), perm: 'shipment.read' },
      { to: '/rep-ops', label: 'Representative Ops', icon: i(Mailbox), perm: 'rep.ops' },
      { to: '/tickets', label: 'Support Tickets', icon: i(LifeBuoy), perm: 'ticket.read' },
      { to: '/escalations', label: 'Escalation Inbox', icon: i(Inbox), perm: 'ticket.assign' },
      { to: '/bank-calls', label: 'Bank Calls', icon: i(Phone), perm: 'bank_call.read' },
      { to: '/whatsapp', label: 'WhatsApp Inbox', icon: i(MessageCircle), anyPerm: ['whatsapp.read', 'whatsapp.manage'] },
      { to: '/communications', label: 'Client Communications', icon: i(MessagesSquare), perm: 'communication.read' },
      { to: '/checklist', label: 'Daily Checklist', icon: i(ClipboardCheck), perm: 'checklist.use' },
    ],
  },
  {
    title: 'Client Services',
    items: [
      { to: '/services?type=credit_building', label: 'Credit Building', icon: i(TrendingUp), anyPerm: ['services.read', 'services.manage'] },
      { to: '/services/credit/bank-applications', label: 'Bank applications', icon: i(Landmark), anyPerm: ['services.read', 'services.manage', 'services.bank_applications'] },
      { to: '/services/global-transfer', label: 'Global Transfer', icon: i(Globe), anyPerm: ['services.read', 'services.manage', 'services.banker', 'services.global_transfer'] },
      { to: '/services/idpack', label: 'ID Packs', icon: i(FileStack), anyPerm: ['idpack.request', 'idpack.submit'] },
      { to: '/services/credit', label: 'Credit Monitoring', icon: i(Gauge), perm: 'credit.read' },
      { to: '/services/payments', label: 'Payment Requests', icon: i(Send), perm: 'opspay.request' },
      { to: '/services/payees', label: 'Saved Payees', icon: i(Landmark), anyPerm: ['opspay.request', 'opspay.approve', 'opspay.pay'] },
      { to: '/services/banker', label: 'Banker Calls', icon: i(Phone), anyPerm: ['services.banker'] },
      { to: '/services/supervisor', label: 'Services Supervision', icon: i(Radar), anyPerm: ['opspay.approve'] },
      { to: '/services/reports', label: 'Services Reports', icon: i(FileBarChart), anyPerm: ['services.read', 'services.manage'] },
    ],
  },
  {
    title: 'Network',
    items: [
      { to: '/clients', label: 'Clients', icon: i(Users), perm: 'client.read' },
      { to: '/representatives', label: 'Representatives', icon: i(Building2), perm: 'representative.read' },
      { to: '/addresses', label: 'Address Inventory', icon: i(MapPin), perm: 'address.read' },
      { to: '/contracts', label: 'Contracts', icon: i(FileSignature), perm: 'contract.read' },
      { to: '/leases', label: 'Leases', icon: i(KeyRound), perm: 'lease.read' },
    ],
  },
  {
    title: 'Finance',
    items: [
      { to: '/finance', label: 'Finance Overview', icon: i(Landmark), perm: 'finance.read' },
      { to: '/finance/ledger', label: 'Ledger', icon: i(BookOpen), perm: 'finance.read' },
      { to: '/finance/payroll', label: 'Payroll', icon: i(Banknote), perm: 'finance.read' },
      { to: '/finance/invoices', label: 'Invoices', icon: i(Receipt), perm: 'finance.read' },
      { to: '/finance/credit', label: 'Client Credit', icon: i(PiggyBank), perm: 'finance.read' },
      { to: '/rep-ops?tab=settlements', label: 'Weekly Settlements', icon: i(Wallet), anyPerm: ['rep.settlement_verify', 'rep.settlement_approve', 'rep.settlement_pay'] },
      { to: '/finance/ops-payments', label: 'Operational Payments', icon: i(AlarmClock), anyPerm: ['opspay.pay', 'opspay.approve'] },
      // One place to pay representatives (new payments, approvals, release, history and the older payment requests).
      { to: '/payouts', label: 'Representative Payments', icon: i(CreditCard), anyPerm: ['payout.read', 'payment.read'] },
      { to: '/payouts/payroll-review', label: 'Payroll Review', icon: i(ClipboardCheck), perm: 'payout.review' },
      { to: '/payouts/final-approvals', label: 'Final Payment Approvals', icon: i(Shield), perm: 'payout.final_approve' },
      { to: '/finance/reports', label: 'Financial Reports', icon: i(FileBarChart), perm: 'finance.read' },
    ],
  },
  {
    title: 'Reporting',
    items: [
      { to: '/reports', label: 'Daily Reports', icon: i(FileText), anyPerm: ['report.submit', 'report.review'] },
      { to: '/analytics', label: 'Analytics', icon: i(BarChart3), perm: 'analytics.read' },
    ],
  },
  {
    title: 'Administration',
    items: [
      { to: '/employees', label: 'Employee Management', icon: i(CalendarCheck), perm: 'employee.read' },
      { to: '/admin/users', label: 'Employees & Access', icon: i(Users), perm: 'admin.users' },
      { to: '/admin/announcements', label: 'Announcements', icon: i(Megaphone), perm: 'announcement.manage' },
      { to: '/admin/settings', label: 'Rules & Settings', icon: i(Settings), perm: 'admin.settings' },
      { to: '/admin/lease-templates', label: 'Lease templates', icon: i(ScrollText), perm: 'lease.template' },
      { to: '/admin/contract-templates', label: 'Contract templates', icon: i(FileSignature), perm: 'contract.template' },
      { to: '/admin/integrations', label: 'Integrations', icon: i(Radar), perm: 'admin.integrations' },
      { to: '/admin/audit', label: 'Audit Log', icon: i(Shield), perm: 'admin.audit' },
      { to: '/admin/delivery-log', label: 'Notifications: Delivery log', icon: i(Send), anyRole: ['system_administrator', 'operations_supervisor'] },
    ],
  },
];

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const { can, hasRole } = useAuth();
  return (
    <nav className="flex h-full flex-col bg-sidebar text-sidebar-text">
      <Link to="/" className="flex items-center gap-2.5 px-5 py-5" onClick={onNavigate}>
        <img src="/icons/icon-192.png" alt="" className="size-8 rounded-lg" />
        <div className="leading-tight">
          <p className="font-brand text-[17px] font-medium text-[#f7f1e1]">Foreign Founders</p>
          <p className="text-[11px] uppercase tracking-wider text-sidebar-text/70">Operations</p>
        </div>
      </Link>
      <div className="flex-1 space-y-5 overflow-y-auto px-3 pb-6">
        {NAV.map((g) => {
          const items = g.items.filter((it) => (it.perm ? can(it.perm) : it.anyPerm ? it.anyPerm.some(can) : it.anyRole ? it.anyRole.some(hasRole) : true));
          if (!items.length) return null;
          return (
            <div key={g.title}>
              <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-text/50">{g.title}</p>
              <ul className="space-y-0.5">
                {items.map((it) => (
                  <li key={it.to}>
                    <NavLink
                      to={it.to}
                      end={it.to.startsWith('/services')}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        clsx('flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] transition-colors', isActive && navQueryMatches(it.to, location.search) ? 'bg-sidebar-active font-medium text-white' : 'hover:bg-sidebar-active/60 hover:text-white')
                      }
                    >
                      {it.icon}
                      {it.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Global search
// ---------------------------------------------------------------------------
interface SearchHit {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  link: string;
}

function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');
  const navigate = useNavigate();
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const { data, isFetching } = useQuery({ queryKey: ['search', debounced], queryFn: () => api.get<SearchHit[]>(`/api/search?q=${encodeURIComponent(debounced)}`), enabled: debounced.length >= 2 });
  return (
    <Popover.Root open={open && debounced.length >= 2} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            ref={ref}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search shipments, tracking #, tickets…"
            aria-label="Global search"
            className="h-9 w-full rounded-lg border border-border bg-surface-2 pl-9 pr-12 text-sm placeholder:text-muted focus:border-ring focus:bg-surface focus:outline-none"
          />
          <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-border px-1.5 text-[10px] text-muted sm:block">Ctrl K</kbd>
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} onOpenAutoFocus={(e) => e.preventDefault()} className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[320px] rounded-xl border border-border bg-surface p-1.5 shadow-xl">
          {isFetching && !data ? (
            <p className="px-3 py-2 text-sm text-muted">Searching…</p>
          ) : !data?.length ? (
            <p className="px-3 py-2 text-sm text-muted">No matches</p>
          ) : (
            <ul>
              {data.map((h) => (
                <li key={`${h.type}-${h.id}`}>
                  <button
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-2"
                    onClick={() => {
                      setOpen(false);
                      setQ('');
                      navigate(h.link);
                    }}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{h.title}</span>
                      {h.subtitle && <span className="block truncate text-xs text-muted">{h.subtitle}</span>}
                    </span>
                    <Badge>{h.type}</Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ---------------------------------------------------------------------------
// Notification center
// ---------------------------------------------------------------------------
export interface NotificationRow {
  id: string;
  category: string;
  severity: 'info' | 'normal' | 'urgent';
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
  deliveries: { channel: string; status: string; error: string | null; via?: string | null; mode?: string | null; note?: string | null }[];
}

function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ['notifications', 'bell'], queryFn: () => api.get<{ unread: number; items: NotificationRow[] }>('/api/notifications?limit=20'), refetchInterval: 120_000 });
  const seen = useRef<Set<string> | null>(null);
  // Toast new urgent alerts as they arrive.
  useEffect(() => {
    if (!data) return;
    if (!seen.current) {
      seen.current = new Set(data.items.map((n) => n.id));
      return;
    }
    for (const n of data.items) {
      if (seen.current.has(n.id)) continue;
      seen.current.add(n.id);
      if (!n.readAt) {
        const fn = n.severity === 'urgent' ? toast.error : toast;
        fn(n.title, { description: n.body ?? undefined, action: n.link ? { label: 'Open', onClick: () => navigate(n.link!) } : undefined });
      }
    }
  }, [data, navigate]);
  const markRead = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) => api.post('/api/notifications/read', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className="relative rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-text" aria-label="Notifications">
        <Bell className="size-5" />
        {!!data?.unread && <span className="tabular absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">{data.unread > 99 ? '99+' : data.unread}</span>}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={8} className="z-50 w-[380px] max-w-[calc(100vw-1rem)] rounded-xl border border-border bg-surface shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="text-sm font-semibold">Notifications</p>
            <button className="text-xs font-medium text-primary hover:underline disabled:opacity-50" disabled={!data?.unread} onClick={() => markRead.mutate({ all: true })}>
              Mark all read
            </button>
          </div>
          <ul className="max-h-[420px] overflow-y-auto">
            {!data?.items.length && <li className="px-4 py-8 text-center text-sm text-muted">You're all caught up.</li>}
            {data?.items.map((n) => (
              <li key={n.id}>
                <button
                  className={clsx('flex w-full gap-3 border-b border-border px-4 py-3 text-left last:border-0 hover:bg-surface-2', !n.readAt && 'bg-primary-soft/40')}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate({ ids: [n.id] });
                    setOpen(false);
                    if (n.link) navigate(n.link);
                  }}
                >
                  <span className={clsx('mt-1.5 size-2 shrink-0 rounded-full', n.severity === 'urgent' ? 'bg-danger' : n.readAt ? 'bg-transparent' : 'bg-primary')} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{n.title}</span>
                    {n.body && <span className="mt-0.5 block line-clamp-2 text-xs text-muted">{n.body}</span>}
                    <span className="mt-1 block text-[11px] text-muted">{relativeTime(n.createdAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <Link to="/notifications" onClick={() => setOpen(false)} className="block border-t border-border px-4 py-2.5 text-center text-xs font-medium text-primary hover:bg-surface-2">
            Open notification center
          </Link>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function LiveIndicator() {
  const s = useRealtimeStatus();
  const map = { live: ['bg-success', 'Live'], connecting: ['bg-warning', 'Connecting'], offline: ['bg-danger', 'Reconnecting'] } as const;
  const [dot, label] = map[s];
  return (
    <span className="hidden items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-muted md:inline-flex" title="Dashboards update automatically when data changes">
      <span className={clsx('size-2 rounded-full', dot, s === 'live' && 'animate-pulse')} />
      {label}
    </span>
  );
}

function ThemeToggle() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  return (
    <button
      className="rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-text"
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => {
        const next = !dark;
        setDark(next);
        document.documentElement.classList.toggle('dark', next);
        try {
          localStorage.setItem('ea-theme', next ? 'dark' : 'light');
        } catch {
          /* storage unavailable */
        }
      }}
    >
      {dark ? <Sun className="size-5" /> : <Moon className="size-5" />}
    </button>
  );
}

function ProfileMenu() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <Dropdown.Root>
      <Dropdown.Trigger className="flex items-center gap-2 rounded-lg p-1 pr-2 hover:bg-surface-2" aria-label="Profile menu">
        <Avatar name={me.fullName} />
        <span className="hidden text-left leading-tight lg:block">
          <span className="block text-sm font-medium">{me.fullName}</span>
          <span className="block text-[11px] text-muted">{me.roles[0]?.label}</span>
        </span>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="end" sideOffset={6} className="z-50 min-w-[220px] rounded-xl border border-border bg-surface p-1.5 shadow-xl">
          <div className="px-3 py-2">
            <p className="text-sm font-medium">{me.fullName}</p>
            <p className="text-xs text-muted">{me.email}</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {me.roles.map((r) => (
                <Badge key={r.key} tone="primary">
                  {r.label}
                </Badge>
              ))}
            </div>
          </div>
          <Dropdown.Separator className="my-1 h-px bg-border" />
          <Dropdown.Item className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-surface-2" onSelect={() => navigate('/profile')}>
            <UserCircle className="size-4" /> Profile & notifications
          </Dropdown.Item>
          <Dropdown.Item
            className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-danger outline-none data-[highlighted]:bg-danger-soft"
            onSelect={async () => {
              await api.post('/api/auth/logout');
              qc.clear();
              navigate('/login');
            }}
          >
            <LogOut className="size-4" /> Sign out
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

export function AppShell() {
  const { me } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useRealtime(!!me);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  return (
    <div className="flex h-full">
      <aside className="hidden w-64 shrink-0 lg:block">
        <div className="fixed inset-y-0 w-64">
          <Sidebar />
        </div>
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 shadow-xl">
            <button className="absolute right-3 top-4 z-10 rounded-md p-1 text-white" onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <X className="size-5" />
            </button>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <ViewAsBanner />
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-surface/90 px-4 backdrop-blur sm:px-6">
          <button className="rounded-lg p-2 text-muted hover:bg-surface-2 lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Menu className="size-5" />
          </button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-1.5">
            <ViewAsButton />
            <LiveIndicator />
            <ThemeToggle />
            <NotificationBell />
            <ProfileMenu />
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {ANNOUNCEMENT_PATHS.includes(location.pathname) && <AnnouncementCards className="mb-6" />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
